import { CircularProgress, Grid2, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import StatusBox from "../../../components/StatusBox";
import { useTranslate } from "../../../hooks/useTranslate";
import Signees, { SigneesStatus } from "./components/Signees";
import Agreements from "./components/Agreements";
import { useStudents } from "../../../context/students";
import { useGetContract } from "../../../hooks/useGetContract";
import Preloader from "../../../components/Preloader";
import { useGetStudentContacts } from "../../../hooks/useGetStudentContacts";
import { useAuth } from "../../../auth";
import EmptyContent from "../../../components/EmptyContent";
import { useGetContractEmbedded } from "../../../hooks/useGetContractEmbedded";
import "./EnrolmentAgreement.css";
import LockedPage from "../../../components/LockedPage";
import useStatuses from "../../../context/status/useStatuses";
import VerificationStatusChip from "../../../components/VerificationStatusChip";
import { EVENT_COMPLETE } from "../DocusignComplete/DocusignComplete";
import DocuSignEmbedded from "../../../components/DocuSignEmbedded";
import { useGetContractPolling } from "../../../hooks/useGetContractPolling";
import { ErrorOutline } from "@mui/icons-material";

const EnrolmentAgreement = () => {
    const { t } = useTranslate();
    const { userId } = useAuth();
    const { activeStudentId } = useStudents();
    const { isLockedStatus: { enrolmentAgreement: isLocked }, statuses, refetchGeneralStatuses } = useStatuses();
    const {
        isLoading, isRefetching, contract, refetch,
    } = useGetContract(isLocked ? null : activeStudentId);
    const { isLoading: isLoadingContact, contacts } = useGetStudentContacts(isLocked ? null : activeStudentId);

    const contact = useMemo(() => contacts.find((contact) => contact.contactId === userId) || {}, [contacts]);
    const contactPrimary = useMemo(() => contacts.find((contact) => contact.isPrimary) || {}, [contacts]);
    const isPrimaryContact = contact?.isPrimary;
    const showDocAgreement = !!contract && isPrimaryContact;
    const showSignViaEmail = !!contract && !isPrimaryContact;
    const embeddedBody = isPrimaryContact ? {
        emailAddress: contact.emailAddress,
        name: contact.displayName,
        contactId: userId,
    } : null;

    const primarySignee = {
        id: contactPrimary.contactId,
        name: contactPrimary.displayName,
        emailAddress: contactPrimary.emailAddress,
    };
    const [secondarySignee, setSecondarySignee] = useState(null);

    const { isLoading: isLoadingEmbedded, contractEmbedded } = useGetContractEmbedded(
        isPrimaryContact && !statuses.enrolmentAgreement.isSuccess ? contract?.contractId : null,
        activeStudentId,
        embeddedBody,
    );

    const onCompleteAgreements = () => {
        refetch();
        refetchGeneralStatuses();
    };
    const {
        isPolling, contractError, startPolling, signees,
    } = useGetContractPolling({
        studentId: activeStudentId,
        hasSeveralSignees: contract?.signees?.length > 1,
        onComplete: onCompleteAgreements,
    });

    useEffect(() => {
        const handleMessage = (event) => {
            const { docusignEvent } = event.data || {};
            if (docusignEvent === EVENT_COMPLETE) {
                window.location.reload();
            }
        };

        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, []);

    return (
        <div className="page-content">
            <Grid2 container spacing={2}>
                <Grid2 size={{ xs: 12, md: 7, lg: 8 }}>
                    <Typography variant="h1">
                        {t("enrolmentAgreement.title")}
                    </Typography>
                    <Typography variant="body1" className="mt-4" component="div">
                        {t("enrolmentAgreement.description")}
                    </Typography>
                    {(!isLocked && !statuses.enrolmentAgreement.isSuccess) && (
                        <Preloader isLoading={isLoading || isRefetching || isLoadingContact} size={30}>
                            {contract ? (
                                <SigneesStatus
                                    signees={signees ?? contract?.signees}
                                    contacts={contacts}
                                    studentId={activeStudentId}
                                    contractId={contract?.contractId}
                                    hideReEmail={isPolling}
                                />
                            ) : (
                                <Signees
                                    primarySignee={primarySignee}
                                    secondarySignee={secondarySignee}
                                    onChangeSecondarySignee={setSecondarySignee}
                                    contacts={contacts}
                                />
                            )}
                        </Preloader>
                    )}
                </Grid2>
                <Grid2 size={{ xs: 12, md: 5, lg: 4 }}>
                    <StatusBox
                        title={t("enrolmentAgreement.enrolmentAgreementStatus")}
                        isSuccess={statuses.enrolmentAgreement.isSuccess}
                        isWarning={statuses.enrolmentAgreement.isWarning}
                        isInfo={statuses.enrolmentAgreement.isInfo}
                    >
                        <Typography variant="body2">
                            {t("enrolmentAgreement.enrolmentAgreementStatusDescription")}
                        </Typography>
                        <VerificationStatusChip status={statuses.enrolmentAgreement} />
                    </StatusBox>
                </Grid2>
            </Grid2>
            <div className="mt-12">
                <LockedPage isLocked={isLocked}>
                    <Preloader isLoading={isLoading || isRefetching || isLoadingContact || isLoadingEmbedded}>
                        {isPolling && (
                            <div className="checkout-status checkout-status-loading">
                                <div className="checkout-status__icon-loading">
                                    <CircularProgress size={32} />
                                </div>
                                <Typography variant="body1" component="div">
                                    {t("renewalSchoolContract.loadingMessage")}
                                </Typography>
                            </div>
                        )}
                        {contractError && (
                            <div className="checkout-status checkout-status-error">
                                <span className="checkout-status__icon checkout-status__icon-error">
                                    <ErrorOutline />
                                </span>
                                <Typography variant="body1" component="div">
                                    {t("renewalSchoolContract.errorMessage")}
                                </Typography>
                            </div>
                        )}
                        <EmptyContent data={showSignViaEmail ? [] : {}} text={t("enrolmentAgreement.signVieEmail")}>
                            {(showDocAgreement && !statuses.enrolmentAgreement.isSuccess) && (
                                <div className="mt-8">
                                    <EmptyContent
                                        data={contractEmbedded?.url}
                                        text={t("enrolmentAgreement.errorEmbedded")}
                                    >
                                        <DocuSignEmbedded
                                            signingUrl={contractEmbedded?.url}
                                            onSigned={startPolling}
                                        />
                                    </EmptyContent>
                                </div>
                            )}
                            {!showDocAgreement && (
                                <Agreements
                                    primarySignee={primarySignee}
                                    secondarySignee={secondarySignee}
                                    studentId={activeStudentId}
                                    onComplete={onCompleteAgreements}
                                />
                            )}
                        </EmptyContent>
                    </Preloader>
                </LockedPage>
            </div>
        </div>
    );
};

export default EnrolmentAgreement;
